import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  MoreOutlined
} from "@ant-design/icons"
import { Button, Card, Divider, Flex, Skeleton, Typography } from "antd"
import React, { useEffect } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { type BlockerFunction } from "react-router-dom"
import { useParams } from "react-router"
import { Link, Navigate } from "react-router-dom"

import { FixedContainer } from "../../../components/FixedContainer.tsx"
import ReactRouterPrompt from "../../../components/ReactRouterPrompt.tsx"
import { RecordRef } from "../../../components/RecordRef.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { RootState } from "../../../store.ts"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { creatorLabel } from "../../Projects/constants/created.ts"
import { getEntityName } from "../../../utils/user/get-entity-name"
import { LeadPill } from "../../Leads/components/LeadPill.tsx"
import { getFirstVisibleProjectManagementRoute } from "../helpers/project-management-route.ts"
import { typePillStyle } from "../helpers/project-type-pill-accents.ts"
import { fetchProjectsFilterOptions } from "../../Projects/redux/projects.thunks.ts"
import { ProjectNotLoaded } from "../components/ProjectNotLoaded.tsx"
import { WorkspaceUnsavedChangesModal } from "../../Lead/LeadWorkspacePage/cards/WorkspaceUnsavedChangesModal.tsx"
import {
  WorkspaceEditGuardProvider,
  useWorkspaceEditGuard
} from "../../Lead/LeadWorkspacePage/cards/workspace-edit-guard.tsx"
import { OverviewCardIcon } from "../../Lead/LeadWorkspacePage/overview/OverviewCardIcon.tsx"
import {
  ProjectStatusSelector,
  type StatusChangeOptions
} from "../components/ProjectStatusSelector.tsx"
import { ProjectStatusTag } from "../components/ProjectStatusTag.tsx"
import {
  canChangeProjectStatus,
  canCloseProject
} from "../helpers/project-edit-visibility.ts"
import { sendStatusChange } from "../helpers/send-status-change.ts"
import {
  type AssignedManager,
  type ManagerField,
  ProjectManagersFields
} from "./overview/ProjectManagersFields.tsx"
import { ProjectOverviewCards } from "./overview/ProjectOverviewCards.tsx"
import {
  addProjectTypeToProject,
  fetchProject,
  removeProjectTypeToProject,
  updateProject
} from "../redux/project.thunks.ts"
import { type ProjectUpdateData } from "../types/project.types.ts"
import { typeLabel } from "../helpers/repeated-project-type.ts"

const { Title, Text } = Typography

const ProjectEditPageBody = ({
  user,
  project,
  filterOptions,
  projectPermissions,
  assignedPMAllowed,
  assignedSalesAllowed,
  updateProject,
  addProjectType,
  removeProjectType,
  fetching,
  loadError,
  fetchFilterOptions,
  getProjectById
}: ConnectedProps<typeof connector>) => {
  const id = useParams()?.id as string
  const { formatDate, dateFormat } = useDateFormatter()
  const { hasUnsavedChanges, discardAll } = useWorkspaceEditGuard()

  const handleStatusChange = (
    project_status: number,
    options?: StatusChangeOptions
  ) =>
    sendStatusChange(
      (acknowledgement) =>
        updateProject({ id, project_status, ...acknowledgement }).unwrap(),
      options
    )

  const handleCardPatch = async (patch: Partial<ProjectUpdateData>) => {
    try {
      await updateProject({ id, ...patch } as ProjectUpdateData).unwrap()
      return true
    } catch (e) {
      console.error("Update project error:", e)
      return false
    }
  }

  const handleManagersChange = async (
    field: ManagerField,
    next: AssignedManager[]
  ) => {
    try {
      await updateProject({ id, [field]: next } as ProjectUpdateData).unwrap()
    } catch (e) {
      console.error("Update project error:", e)
    }
  }

  useEffect(() => {
    if (id) getProjectById(id)
  }, [id, getProjectById])

  useEffect(() => {
    generateDocumentTitle("Edit Project")
    fetchFilterOptions()
  }, [])

  if (fetching) return <Skeleton.Node className={"size-full"} />

  if (!project) {
    return (
      <ProjectNotLoaded
        id={id}
        loadError={loadError}
        onRetry={() => getProjectById(id)}
      />
    )
  }

  // This route is outside the project layout, so hiding the edit affordances
  // does not stop a bookmark reaching a form the API will refuse to save.
  if (project.is_archived) {
    return <Navigate replace to={RoutesPaths.project.view(id).overview} />
  }

  const customer = project?.customer || project?.temporary_customer
  const typePills = project?.project_type_details?.map(typeLabel) ?? []
  const managementRoute = getFirstVisibleProjectManagementRoute(
    projectPermissions,
    user?.role?.id,
    id
  )

  const blockNavigation: BlockerFunction = (args) => {
    if (!hasUnsavedChanges) return false
    if (!args?.nextLocation) return true
    return args.currentLocation.pathname !== args.nextLocation.pathname
  }

  return (
    <FixedContainer>
      <div className={"grid"}>
        <Flex
          vertical
          gap={"small"}
          className={"max-h-[calc(100vh-90px)] overflow-auto min-w-0"}
        >
          <Card size={"small"} styles={{ body: { padding: "16px 20px" } }}>
            <Flex vertical gap={4}>
              <Link to={RoutesPaths.project.list}>
                <Button
                  type={"text"}
                  size={"small"}
                  icon={<ArrowLeftOutlined />}
                  className={"!px-1 !text-secondary"}
                >
                  Back to Projects
                </Button>
              </Link>

              <Flex
                justify={"space-between"}
                align={"center"}
                gap={"middle"}
                wrap
              >
                <Flex gap={"middle"} align={"center"} className={"min-w-0"}>
                  <OverviewCardIcon
                    icon={<HomeOutlined />}
                    accent={"teal"}
                    size={56}
                  />
                  <Flex vertical gap={2} className={"min-w-0"}>
                    <RecordRef
                      uid={project?.uid}
                      customerName={
                        customer ? getEntityName(customer) : undefined
                      }
                    />
                    <Flex align={"center"} gap={8} wrap>
                      <Title
                        level={3}
                        className={"m-0"}
                        ellipsis={{ tooltip: project?.lead_name }}
                      >
                        {project?.lead_name || "No project name"}
                      </Title>
                      {canChangeProjectStatus(projectPermissions, project) ? (
                        <ProjectStatusSelector
                          status={project?.project_status}
                          statuses={filterOptions?.project_status || []}
                          canClose={canCloseProject(
                            projectPermissions,
                            project
                          )}
                          onChange={handleStatusChange}
                        />
                      ) : (
                        <ProjectStatusTag status={project?.project_status} />
                      )}
                      {typePills.map((name, index) => (
                        <LeadPill key={name} style={typePillStyle(index)}>
                          {name}
                        </LeadPill>
                      ))}
                    </Flex>
                    <Flex gap={8} wrap>
                      {customer && (
                        <Text type={"secondary"}>
                          Customer:{" "}
                          <Text strong>{getEntityName(customer)}</Text>
                        </Text>
                      )}
                      {project?.created && (
                        <Text type={"secondary"}>
                          Created {formatDate(project.created, dateFormat)} by{" "}
                          <Text strong>{creatorLabel(project.created_by)}</Text>
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                </Flex>

                {/* Project Manager / Sales Manager assignment lives in the
                    header now, not a separate Activity block — the old
                    "Activity" tab's only content was ever this assignment,
                    never a real activity feed. Each select saves immediately,
                    the same way the status pill next to it already does.
                    One row, vertically centered against the identity block,
                    with dividers so it reads as one header, not a floating
                    block stacked above the action buttons. */}
                <Flex align={"center"} gap={"middle"}>
                  <ProjectManagersFields
                    project={project}
                    filterOptions={filterOptions}
                    assignedPMAllowed={assignedPMAllowed}
                    assignedSalesAllowed={assignedSalesAllowed}
                    onChange={handleManagersChange}
                  />

                  <Divider
                    type={"vertical"}
                    style={{ height: 40, margin: 0, borderInlineStartWidth: 1 }}
                  />

                  <Flex gap={"small"} align={"center"}>
                    {/* This screen is itself the View/Edit experience — each
                        section edits in place, so there is no separate "Edit
                        Project" button. Project Management (Milestones,
                        Finance, Invoices, ...) is the other, deliberate
                        destination. */}
                    {managementRoute ? (
                      <Link to={managementRoute}>
                        <Button type={"primary"} icon={<AppstoreOutlined />}>
                          Go to Project Management
                        </Button>
                      </Link>
                    ) : (
                      <Button
                        type={"primary"}
                        icon={<AppstoreOutlined />}
                        disabled
                      >
                        Go to Project Management
                      </Button>
                    )}
                    {/* Reserved for future actions (e.g. Archive) — none are
                        part of this step. */}
                    <Button
                      icon={<MoreOutlined />}
                      aria-label={"More actions"}
                      disabled
                    />
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Card>

          <ProjectOverviewCards
            project={project}
            filterOptions={filterOptions}
            projectPermissions={projectPermissions}
            canEdit
            onSave={handleCardPatch}
            addProjectType={addProjectType}
            removeProjectType={removeProjectType}
          />
        </Flex>
      </div>

      <ReactRouterPrompt when={blockNavigation}>
        {({ isActive, onConfirm, onCancel }) => (
          <WorkspaceUnsavedChangesModal
            open={isActive}
            onStay={onCancel}
            onDiscard={() => {
              discardAll()
              onConfirm()
            }}
          />
        )}
      </ReactRouterPrompt>
    </FixedContainer>
  )
}

export const ProjectEditPage = (props: ConnectedProps<typeof connector>) => (
  <WorkspaceEditGuardProvider>
    <ProjectEditPageBody {...props} />
  </WorkspaceEditGuardProvider>
)

const mapStateToProps = (state: RootState) => ({
  user: state.auth?.userInfo,
  fetching: state.project.fetching || state.projects.fetchingFilterOptions,
  project: state.project.project,
  loadError: state.project.loadError,
  filterOptions: state.projects.filterOptions,
  projectPermissions: state.auth.permissions?.codes?.projects,
  // Mirrors the pre-redesign form's own source for these two flags exactly
  // (including its userInfo.permissions staleness quirk) rather than the
  // canonical state.auth.permissions this page uses elsewhere, for parity
  // with the existing assignment behavior being carried over.
  assignedSalesAllowed:
    !!state.auth?.userInfo?.permissions?.codes?.leads?.features
      ?.lead_assign_sales_manager,
  assignedPMAllowed:
    !!state.auth?.userInfo?.permissions?.codes?.projects?.features
      ?.project_assign_pm
})

const mapDispatchToProps = {
  getProjectById: fetchProject,
  updateProject,
  addProjectType: addProjectTypeToProject,
  removeProjectType: removeProjectTypeToProject,
  fetchFilterOptions: fetchProjectsFilterOptions
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectEditPage)
